import React, { useState, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import { FiEdit2, FiSearch, FiPercent, FiCheck, FiX, FiRefreshCw, FiInfo, FiTrendingUp } from "react-icons/fi";
import { baseurl } from "../baseurl";
import axios from "axios";
import Swal from "sweetalert2";

function CommissionManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeEditId, setActiveEditId] = useState(null);
  const [tempCharges, setTempCharges] = useState("");

  const showToast = (title, icon = "success") => {
    Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000 });
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getCategories`);
      setCategories(res.data.categories || []);
    } catch (err) {
      showToast("Failed to load categories", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleEditClick = (category) => {
    setActiveEditId(category.id);
    setTempCharges(parseFloat(category.admin_charges) || 0);
  };

  const handleUpdate = async (id) => {
    if (tempCharges === "" || tempCharges < 0 || tempCharges > 100) {
      return showToast("Enter 0-100%", "warning");
    }
    try {
      await axios.put(`${baseurl}updateCategoryCommission/${id}`, { commission: tempCharges });
      showToast("Margin updated");
      setActiveEditId(null);
      fetchCategories();
    } catch (err) { showToast("Update failed", "error"); }
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header & Filter Bar */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <h5 className="fw-bold mb-0 text-dark">Revenue Splits</h5>
              <p className="text-muted extra-small mb-0">Adjust platform service fees per category</p>
            </div>
            <div className="d-flex gap-2">
              <div className="input-group input-group-sm shadow-sm" style={{ width: "220px" }}>
                <span className="input-group-text bg-white border-0"><FiSearch className="text-muted" /></span>
                <input
                  type="text"
                  className="form-control border-0 ps-0"
                  placeholder="Find category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="btn btn-sm btn-white shadow-sm border-0" onClick={fetchCategories}>
                <FiRefreshCw className={loading ? "spin" : ""} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5 mt-5"><div className="spinner-border text-primary spinner-border-sm"></div></div>
          ) : (
            <div className="row g-3">
              {filteredCategories.map((c) => (
                <div className="col-xl-3 col-lg-4 col-sm-6" key={c.id}>
                  <div className={`card border-0 shadow-sm h-100 position-relative transition-all ${activeEditId === c.id ? 'ring-primary' : ''}`}>
                    
                    {/* Visual Progress Bar (Background) */}
                    <div className="position-absolute top-0 start-0 w-100" style={{height: '4px', background: '#f1f5f9'}}>
                        <div 
                          className="h-100 bg-primary opacity-50" 
                          style={{width: `${activeEditId === c.id ? tempCharges : c.admin_charges}%`, transition: 'width 0.3s ease'}}
                        ></div>
                    </div>

                    <div className="card-body p-3 pt-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="avatar-xs me-2 bg-soft-primary text-primary rounded d-flex align-items-center justify-content-center fw-bold" style={{width: '28px', height: '28px', fontSize: '12px'}}>
                          {c.name.charAt(0)}
                        </div>
                        <h6 className="mb-0 text-truncate fw-bold small flex-grow-1">{c.name}</h6>
                        {activeEditId !== c.id && (
                          <button className="btn btn-link p-0 text-muted" onClick={() => handleEditClick(c)}>
                            <FiEdit2 size={12} />
                          </button>
                        )}
                      </div>

                      {activeEditId === c.id ? (
                        <div className="animate-in">
                          <div className="input-group input-group-sm mb-2 shadow-sm">
                            <input
                              type="number"
                              className="form-control fw-bold border-primary text-center"
                              value={tempCharges}
                              autoFocus
                              onChange={(e) => setTempCharges(e.target.value)}
                            />
                            <span className="input-group-text bg-primary text-white border-primary"><FiPercent size={10}/></span>
                          </div>
                          
                          {/* Live Preview Math */}
                          <div className="bg-light p-2 rounded mb-2 border">
                            <div className="d-flex justify-content-between extra-small mb-1">
                                <span className="text-muted">Admin Gets:</span>
                                <span className="fw-bold text-primary">{tempCharges || 0}%</span>
                            </div>
                            <div className="d-flex justify-content-between extra-small">
                                <span className="text-muted">Store Gets:</span>
                                <span className="fw-bold text-success">{100 - (tempCharges || 0)}%</span>
                            </div>
                          </div>

                          <div className="d-flex gap-1">
                            <button className="btn btn-xs btn-primary w-100" onClick={() => handleUpdate(c.id)}><FiCheck className="me-1"/>Set</button>
                            <button className="btn btn-xs btn-light border w-100" onClick={() => setActiveEditId(null)}><FiX/></button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-end justify-content-between mt-2">
                          <div>
                            <div className="text-muted extra-small text-uppercase fw-bold mb-1">Service Fee</div>
                            <div className="h4 mb-0 fw-bold text-dark">{parseFloat(c.admin_charges).toFixed(1)}%</div>
                          </div>
                          <div className="text-end">
                             <div className="extra-small text-muted mb-1">Store Split</div>
                             <div className="small fw-bold text-success">{(100 - c.admin_charges).toFixed(1)}%</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Compact Info Footer */}
          <div className="mt-4 p-2 px-3 bg-white rounded border border-start-0 border-end-0 border-top-0 border-bottom-1 border-primary shadow-sm d-flex align-items-center">
            <FiInfo className="text-primary me-2" size={14}/>
            <span className="text-muted extra-small">Margins are applied globally to all products under these specific categories during payout calculation.</span>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; font-weight: 600; }
        .bg-soft-primary { background-color: #eef2ff; }
        .btn-xs { padding: 4px 8px; font-size: 10px; font-weight: 700; }
        .btn-white { background: #fff; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ring-primary { ring: 2px solid #4facfe; border: 1px solid #4facfe !important; }
        .animate-in { animation: fadeIn 0.2s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        .transition-all { transition: all 0.2s ease-in-out; }
      `}</style>
    </>
  );
}

export default CommissionManager;